// useLocationCoords — auto-geocodes an address via Nominatim when a
// game/series/trip doc doesn't have latitude/longitude yet.
//
// Returns { latitude, longitude, loading }. If the doc already has non-zero
// coords, we short-circuit and return those. Prevents the map iframe from
// disappearing on events created before geocoding was wired.

import { useEffect, useState } from "react";

const cache = new Map(); // "query" → { latitude, longitude }

export function useLocationCoords({ latitude, longitude, address, location_name, city }) {
  const hasCoords = Boolean(latitude && longitude && latitude !== 0 && longitude !== 0);
  const [coords, setCoords] = useState(hasCoords ? { latitude, longitude } : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (hasCoords) {
      setCoords({ latitude, longitude });
      return;
    }

    // Multi-query strategy: Nominatim's index has gaps — venue names, partial
    // postcodes ("EC2A" without suffix), etc. We try up to three queries,
    // getting progressively more permissive.
    const primary = [location_name, address, city].filter(Boolean).join(", ");
    const secondary = [address, city].filter(Boolean).join(", ");
    // Strip anything that looks like a UK postcode fragment from the address,
    // then re-combine with city as the final fallback.
    const stripPostcode = (s) => (s || "")
      .replace(/\b[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9]?[A-Z]{0,2}\b/gi, "")
      .replace(/,\s*,/g, ",")
      .replace(/,\s*$/g, "")
      .trim();
    const tertiary = [stripPostcode(address), city].filter(Boolean).join(", ");
    const queries = Array.from(new Set([primary, secondary, tertiary].filter(Boolean)));
    if (queries.length === 0) return;

    const cacheKey = queries.join("|");
    if (cache.has(cacheKey)) {
      setCoords(cache.get(cacheKey));
      return;
    }

    let cancelled = false;
    setLoading(true);

    async function lookup(query) {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      if (data && data.length > 0) {
        return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) };
      }
      return null;
    }

    (async () => {
      try {
        let hit = null;
        for (const q of queries) {
          if (cancelled) return;
          hit = await lookup(q);
          if (hit) break;
        }
        if (cancelled) return;
        if (hit) {
          cache.set(cacheKey, hit);
          setCoords(hit);
        }
      } catch (_) { /* fallback link remains */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [hasCoords, latitude, longitude, address, location_name, city]);

  return { ...(coords || { latitude: 0, longitude: 0 }), loading, hasCoords: Boolean(coords) };
}
